import { CommonDialogs } from "@mcleod/common";
import { Button, ButtonProps, ButtonVariant, ClickEvent, Layout, Panel, PanelProps, Table, TableSelectionEvent, Toast } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { Api, Logger, LogManager, McLeodClassicIntegration, ModelRow, Navigation } from "@mcleod/core";
import { MobileCommUnitChangeConfirmation } from "./MobileCommUnitChangeConfirmation";
import { MobileCommUnitList } from "./MobileCommUnitList";

interface ActionButtonProps {
    button: Button;
    /**
     * If the shouldEnable() function is provided, it will be called when the table selection changes.
     * The button will be enabled or disabled based off the return value of this function.
     * If shouldEnable() is not provided, the button will always be enabled when a row is selected.
     * @param modelRow
     */
    shouldEnable?: (table: Table, modelRow: ModelRow) => boolean;
}

const buttonProps: Partial<ButtonProps> = {
    color: "primary", variant: ButtonVariant.round, borderWidth: 0,
    imageWidth: 22, imageHeight: 22, rowBreak: false, enabled: false
}

const borderPanelProps: Partial<PanelProps> = {
    height: 20,
    padding: 0,
    marginRight: 10,
    borderRightWidth: 1,
    borderRightColor: "subtle.light",
    fillRow: true,
    rowBreak: false
}

/**
 * There are 3 steps to adding a new action button:
 * 1. Add a new ActionButtonType enum value.
 * 2. Create a new function that will return the button.
 * 3. Using the new enum value and the function from step 2, create a new ActionButtonProps object and add it to the
 *   buttonProps map in the buildButtonMap() function.  See the comment in ActionButtonProps interface for more information.
 */

export enum ActionButtonType {
    CHANGE,
    FILTERS,
    GROUP,
    REEFER_SETTINGS
}

const log: Logger = LogManager.getLogger("MobileCommUnitToolPanel");
export class MobileCommUnitToolPanel extends Panel {
    private layout: MobileCommUnitList;
    private table: Table;
    private buttonMap: Map<ActionButtonType, ActionButtonProps>;
    private buttonTypes: ActionButtonType[];

    constructor(layout: MobileCommUnitList, ...buttonsTypes: ActionButtonType[]) {
        super({ marginLeft: 15, paddingTop: 0, paddingBottom: 0 });

        this.layout = layout;
        this.table = layout.tableMCUnit;
        this.buttonTypes = buttonsTypes;
        if (buttonsTypes.length > 0) {
            this.buildButtonMap();
            this.addActionButtons();
            this.table.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
        }
        this.table.addCenterTool(this);
    }

    private onSelectionChanged(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data as ModelRow;
        this.buttonTypes.forEach(buttonType => {
            const props = this.buttonMap.get(buttonType);
            if (props.shouldEnable != null) {
                props.button.enabled = props.shouldEnable(this.table, modelRow);
            } else {
                props.button.enabled = true;
            }
        })
    }

    /**
     * When adding a new action button, add it to the buttonProps map here.
     * @private
     */
    private buildButtonMap() {
        this.buttonMap = new Map();

        this.buttonMap.set(ActionButtonType.CHANGE, {
            button: this.getChangeButton(), shouldEnable(table, modelRow) {
                return table.selectedRows.length > 0;
            }
        });

        this.buttonMap.set(ActionButtonType.FILTERS, {
            button: this.getFiltersButton(), shouldEnable(table, modelRow) {
                if (table.selectedRows.length == 1) {
                    return (modelRow.get("groups") !== undefined)
                } else {
                    return false;
                }
            }
        });

        this.buttonMap.set(ActionButtonType.GROUP, {
            button: this.getGroupButton(), shouldEnable(table, modelRow) {
                if (table.selectedRows.length == 1) {
                    return (modelRow.get("groups") !== undefined)
                } else {
                    return false;
                }
            }
        });

        this.buttonMap.set(ActionButtonType.REEFER_SETTINGS, {
            button: this.getReeferSettingsButton(), shouldEnable(table, modelRow) {
                const selectedRows = table.selectedRows;
                let enableButton = true;

                if (selectedRows.length == 1) {
                    if (modelRow.get("mc_unit_type") != "L") {
                        enableButton = false;
                    }

                    if (modelRow.get("control_is_active") === false) {
                        enableButton = false;
                    }

                    if (modelRow.get("has_reefer_tracking") === false) {
                        enableButton = false;
                    }

                    if (modelRow.get("trailer.reefer_id") === undefined) {
                        enableButton = false;
                    }

                    const controlID = modelRow.get("mc_control_id") as string;
                    if (controlID && !controlID.startsWith("ORB")) {
                        enableButton = false;
                    }
                } else {
                    enableButton = false;
                }

                return enableButton;
            }
        });
    }

    /**
     * This adds the action buttons to the panel.
     */
    async addActionButtons() {
        this.add(new Panel({ ...borderPanelProps, marginLeft: 0, marginRight: 10 }));
        this.buttonTypes.forEach(buttonType => this.add(this.buttonMap.get(buttonType).button));
        this.add(new Panel({ ...borderPanelProps, marginLeft: 10, marginRight: 0 }));
        if (this.components.length == 2) {
            this.removeAll();
        }
    }

    public getButton(buttonType: ActionButtonType): Button {
        return this.buttonMap.get(buttonType).button;
    }

    private getChangeButton(): Button {
        const buttonChange = new Button({
            ...buttonProps,
            id: "buttonChange",
            imageName: ImageName.change,
            enabled: false,
            tooltip: "Change"
        });
        buttonChange.addClickListener((event: ClickEvent) => this.changeOnClick(event));
        return buttonChange;
    }

    private getFiltersButton(): Button {
        const buttonFilters = new Button({
            ...buttonProps,
            id: "buttonFilters",
            imageName: ImageName.profileFilter,    // funnel
            enabled: false,
            tooltip: "Filters"
        });
        buttonFilters.addClickListener((event: ClickEvent) => this.filtersOnClick(event));
        return buttonFilters;
    }

    private getGroupButton(): Button {
        const buttonGroup = new Button({
            ...buttonProps,
            id: "buttonGroup",
            imageName: ImageName.links,
            enabled: false,
            tooltip: "Group"
        });
        buttonGroup.addClickListener((event: ClickEvent) => this.groupOnClick(event));
        return buttonGroup;
    }

    private getReeferSettingsButton(): Button {
        const buttonReeferSettings = new Button({
            ...buttonProps,
            id: "buttonReefer",
            imageName: ImageName.settings,
            enabled: false,
            tooltip: "Reefer Settings"
        });
        buttonReeferSettings.addClickListener((event: ClickEvent) => this.reeferSettingsOnClick(event));
        return buttonReeferSettings;
    }

    changeOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        const layout = Layout.getLayout("lme/dispatch/mobilecomm/MobileCommUnitChangeConfirmation") as MobileCommUnitChangeConfirmation;
        const data = selectedRow.data;
        const confirmDialog = CommonDialogs.createDialog(layout, {
            title: `Mobile Comm Unit Change`,
            okVisible: true,
            okProps: {
                marginRight: 20
            }
        });

        layout.addLayoutLoadListener(()=>{
            layout.textboxUnitNumber.focus();
        });

        confirmDialog.show().then(()=>Promise.resolve(confirmDialog).then(async result => {
            if (!result.wasCancelled) {
                const originUnitNumber = data.get("id");
                const destUnitNumber = layout.textboxUnitNumber.lookupModelData != null ? layout.textboxUnitNumber.lookupModelData[0].get("id") : null;
                const unitType =  layout.textboxUnitType.selectedItem.value;
                const unitTypeDisplay =  layout.textboxUnitType.selectedItem.displayValue;
                Api.post("lme/dispatch/mobilecomm/unit-change", {
                    origin_mc_unit: originUnitNumber,
                    dest_mc_unit: destUnitNumber,
                    mc_unit_type: unitType
                }).then((response) => {
                    Toast.showSuccessToast("Changed unit " + originUnitNumber + " to unit number " + destUnitNumber + " with unit type of  " + unitTypeDisplay);
                    this.table.selectedRows = null;
                    this.layout.mainDataSource.search(this.layout.mainDataSource.lastSearch);
                });
            }
        }));
    }

    private filtersOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        const data = selectedRow.data;
        const groups = data.get("groups");

        groups.map(value => value["group_id"]) .forEach(groupId => {
            Navigation.navigateTo("lme/dispatch/mobilecomm/MobileCommGroupFilter?mode=update&key=" + groupId, {newTab: true});
        })
    }

    private groupOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        const data = selectedRow.data;
        const groups = data.get("groups");

        const joinedGroup = groups.map(value => value["group_id"]) .forEach(groupId => {
            Navigation.navigateTo("lme/dispatch/mobilecomm/MobileCommGroup?mode=update&key=" + groupId, {newTab: true});
        })
    }

    private reeferSettingsOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.mc.PanelMcTrailerReeferSettings", selectedRow.data.get("id"));
    }
}
